import { Dispatch, SetStateAction, useContext, useMemo } from "react";
import currency from "currency.js";
import { useTranslation } from "react-i18next";
import {
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../../codegen-api";
import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { isValidDecimalInput } from "../../utils/decimalString";
import SegmentControl from "../shared/SegmentedControl";
import { QuickActions, SlippageControl } from "../shared/NumericField";
import { SettingsContext } from "../../contexts/SettingsContext";
import { closePositionMax } from "../../utils/amount/max/closePositionMax";
import { worstFillPrice } from "../../utils/amount/slippage";
import { activePreset } from "../../utils/amount/percent";
import {
  IClosePrecision,
  closeSizeExceedsPosition,
  closeSizeForPercent,
  closeSteps,
} from "./ClosePositionModal.perpAction";
import {
  CloseControlsWrapper,
  CloseFieldLabel,
  CloseInputRow,
  CloseInputUnit,
} from "./style";

const CLOSE_STOPS = [25, 50, 75, 100];

type TranslateFn = (...args: any[]) => any;

interface IPerpCloseControlsProps {
  closeMode: "market" | "limit";
  setCloseMode: Dispatch<SetStateAction<"market" | "limit">>;
  limitPriceInput: string;
  setLimitPriceInput: Dispatch<SetStateAction<string>>;
  closeSizeInput: string;
  setCloseSizeInput: Dispatch<SetStateAction<string>>;
  closeSizeNum: number;
  positionAmount: number;
  closePrecision: IClosePrecision;
  position: GetAccount200ResponsePositionsInner;
  /** The book price a market close would cross; absent when the side is empty. */
  closeReferencePrice?: string;
  t: TranslateFn;
}

export function PerpCloseControls({
  closeMode,
  setCloseMode,
  limitPriceInput,
  setLimitPriceInput,
  closeSizeInput,
  setCloseSizeInput,
  closeSizeNum,
  positionAmount,
  closePrecision,
  position,
  closeReferencePrice,
  t,
}: IPerpCloseControlsProps) {
  // A market close is bounded by the same max slippage as the trade form; it
  // is shown here so a trader refused in a fast market can loosen it on the spot.
  const { slippageBps, setSlippageBps } = useContext(SettingsContext);
  const { t: slippageT } = useTranslation("app", {
    keyPrefix: "common.inputs.slippage",
  });

  // Closing a LONG sells, so its bound sits BELOW the bid; closing a SHORT
  // buys, so it sits above the ask. Same maths the trade form shows.
  const worstFill = useMemo(() => {
    const isBuy = position.side !== SideResponse.Buy;
    const worst = worstFillPrice(
      closeReferencePrice ?? null,
      slippageBps,
      isBuy,
      closePrecision.price_precision
    );
    return {
      label: slippageT("worst_fill"),
      valueText:
        worst === null
          ? undefined
          : currency(worst, { precision: closePrecision.price_precision }).format(),
    };
  }, [closePrecision.price_precision, closeReferencePrice, position.side, slippageBps, slippageT]);
  // MAX is the position, exactly; 25/50/75 floor onto the grid. The lit chip
  // is the one whose own output equals the field — exact, where the old float
  // "within half a percent" test lit two chips on a coarse grid.
  const steps = useMemo(() => closeSteps(closePrecision), [closePrecision]);
  const closeMax = useMemo(
    () => closePositionMax({ positionAmount: position.amount, steps, unit: position.asset }),
    [position.amount, position.asset, steps]
  );
  const activeStop = closeMax.ok
    ? activePreset(closeSizeInput, closeMax.max, CLOSE_STOPS, steps.amountStep)
    : null;
  return (
    <CloseControlsWrapper>
      <SegmentControl
        segments={[
          {
            value: "market",
            display: t("market", { defaultValue: "Market" }),
            textColor:
              closeMode === "market"
                ? TEXT_COLORS.one
                : TEXT_COLORS.three,
          },
          {
            // Limit close = opposite-side limit via placePerpOrder.
            // Passes reduceOnly intent; the contract will enforce it
            // once reduce-only is enabled on-chain (see usePerpOrder).
            value: "limit",
            display: t("limit", { defaultValue: "Limit" }),
            textColor:
              closeMode === "limit"
                ? TEXT_COLORS.one
                : TEXT_COLORS.three,
          },
        ]}
        value={closeMode}
        onSelect={(v) => setCloseMode(v as "market" | "limit")}
        config={{
          color: TEXT_COLORS.three,
          widthType: "fullWidth",
          backgroundColor: LAYER_COLORS.two,
          activeBackgroundColor: COLORS.highlight.four,
          borderRadius: "6px",
          button: { height: 36, fontSize: "13px" },
        }}
      />
      {closeMode === "limit" && (
        <div>
          <CloseFieldLabel>
            {t("limit_price", { defaultValue: "Limit Price" })}
          </CloseFieldLabel>
          <CloseInputRow error={Number(limitPriceInput) <= 0}>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0"
              value={limitPriceInput}
              onChange={(e) => setLimitPriceInput(e.target.value)}
            />
            <CloseInputUnit>USD</CloseInputUnit>
          </CloseInputRow>
        </div>
      )}
      <div>
        <CloseFieldLabel>
          {t("close_size", { defaultValue: "Size to Close" })}
        </CloseFieldLabel>
        <CloseInputRow
          error={
            // Exponent text ("1e-7") is a number to the input but not a size.
            !isValidDecimalInput(closeSizeInput) ||
            closeSizeNum <= 0 ||
            closeSizeExceedsPosition(closeSizeInput, position.amount)
          }
        >
          <input
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={closeSizeInput}
            onChange={(e) => setCloseSizeInput(e.target.value)}
          />
          <CloseInputUnit>{position.asset}</CloseInputUnit>
        </CloseInputRow>
        <div style={{ marginTop: SPACING.two }}>
          <QuickActions
            active={activeStop}
            max={closeMax}
            onSelect={(pct) =>
              setCloseSizeInput(closeSizeForPercent(position.amount, pct, closePrecision))
            }
          />
        </div>
      </div>
      {closeMode === "market" && (
        <SlippageControl
          bps={slippageBps}
          onChange={setSlippageBps}
          collapsible
          consequence={worstFill}
        />
      )}
    </CloseControlsWrapper>
  );
}
